import React from 'react';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { Map as LeafletMap, TileLayer, Marker } from 'react-leaflet';
// @ts-ignore
import { divIcon } from 'leaflet';

import { CoreLink } from '@uncinc/react-drupal-core';
import { Container, Icon, SearchKit } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import MapboxLayer from '../../../components/Search/NLdoet/ProjectSearch/Map/MapboxLayer';
import MapListSearchResults from '../../../components/Search/NLdoet/ProjectSearch/Results/MapList';

import {
  WysiwygField,
  ButtonWrapper,
  PatternBlock,
  Share,
} from '../../../components';
import type { NodeNLdoetProject } from '../../../types/uncinc_profile/nodes';

import { APP_OPTIONS } from '../../../config/constants';

import './style.scss';

import * as query from '../../../components/Search/NLdoet/ProjectSearch/query.graphql';

export interface NLdoetProjectProps {
  entity: ImmutableEntity<NodeNLdoetProject>;
}

const NLdoetProject: React.FC<NLdoetProjectProps> = ({ entity }) => {
  const { t } = useTranslation();
  const project = entity.toJS();

  // Extract project data
  const title = project.title[0].value;
  const body = project.body[0].processed;
  const result = project.field_result[0]?.processed;
  const address = project.field_nldoet_project_address[0];
  const extraInformation = project.field_extra_information[0]?.processed;
  const maxVolunteers = project.field_max_volunteers[0]?.value;
  const placesAvailable = project.places_available[0]?.value || 0;
  const location = project.field_project_location[0]?.entity.name[0].value;
  const website = project.field_website[0]?.uri;
  const contactGender = project.field_contact_gender[0]?.value;
  const contactInitials = project.field_contact_initials[0]?.value;
  const contactSurname = project.field_contact_surname[0]?.value;
  const contactEmail = project.field_contact_email[0]?.value;
  const contactPhoneNumber = project.field_contact_phone_number[0]?.value;
  const salesforceRecordId = project.field_sf_project_id[0]?.value;
  // @ts-ignore
  const organization = address?.organization;

  // Map configuration
  const accessToken = config.get('MAPBOX_API_KEY');
  const userName = config.get('MAPBOX_USERNAME');
  const styleName = config.get('MAPBOX_STYLENAME');
  const geolocation = project.field_project_geolocation?.[0];
  const lat = geolocation?.lat || 52.3676;
  const lon = geolocation?.lon || 4.9041;

  const customIcon = divIcon({
    className: 'Icon',
    html: '<span class="icon icon-marker icon-variant"></span>',
  });

  // Date and time formatting
  const dateInfo = project.field_activity_datetime?.[0];
  const startDate = new Date(dateInfo.value);
  const endDate = new Date(dateInfo.end_value);
  const dateString = startDate.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).replace(/^\w/, (c: string) => c.toUpperCase());
  const startTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
  const endTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
  const isSaturday = startDate.getDay() === 6;

  const params = `?projectCode=${salesforceRecordId}&projectName=${title}`;


  return (
    <div className="NLdoetProject">
      <Container>
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <PatternBlock className="DefaultPatternBlock">
              <h2>{title}</h2>
              <WysiwygField
                property="schema:text"
                className="DefaultPatternBlock__body"
                html={body}
              />
              {placesAvailable > 0 && (
                <div className="DefaultPatternBlock__buttons">
                  <ButtonWrapper
                    // @ts-ignore
                    href={`${APP_OPTIONS.parameters.nldoetSubscribeUrls.solo[isSaturday ? 'saturday' : 'friday']}${params}`}
                    type="secondary"
                  >
                    {t('nldoet.project.register_individual')}
                  </ButtonWrapper>

                  <ButtonWrapper
                    // @ts-ignore
                    href={`${APP_OPTIONS.parameters.nldoetSubscribeUrls.group[isSaturday ? 'saturday' : 'friday']}${params}`}
                    type="secondary"
                    inverted
                  >
                    {t('nldoet.project.register_group')}
                  </ButtonWrapper>
                </div>
              )}
            </PatternBlock>
            <div className="row">
              <div className="col-12 col-lg-4 col-xl-3">
                <div className="NLdoetProject__map">
                  {(lat && lon) && (
                    <LeafletMap
                      center={[lat, lon]}
                      zoom={16}
                      style={{ height: '300px', width: '100%' }}
                      zoomControl={false}
                      dragging={false}
                      touchZoom={false}
                      doubleClickZoom={false}
                      scrollWheelZoom={false}
                      boxZoom={false}
                      keyboard={false}
                    >
                      <MapboxLayer
                        // @ts-ignore
                        accessToken={APP_OPTIONS?.parameters.mapboxApiKey}
                        /* eslint-disable */
                        style="mapbox://styles/oranjefonds/clagrscum003u14o3cd31m9si"
                      />
                      <Marker position={[lat, lon]} icon={customIcon} />
                    </LeafletMap>
                  )}
                </div>
                <div className="NLdoetProject__sidebar">
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="location_on" flair="filled" /></div>
                    <div className="NLdoetProject__sidebar_item__content">
                      {address?.address_line1} {address?.address_line2} {address?.postal_code} {address?.locality}
                      <div className="NLdoetProject__sidebar_item__content__link">
                        <CoreLink href={`https://www.google.com/maps/dir/?api=1&destination=${address?.address_line1}+${address?.address_line2}+${address?.postal_code}+${address?.locality}`}>
                          Route
                        </CoreLink>
                      </div>
                    </div>
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="calendar_today" flair="filled" /></div>
                    {dateString}
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="schedule" flair="filled" /></div>
                    {startTime} - {endTime}
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="people" flair="filled" /></div>
                    {t('nldoet.project.available_of_total', { available: placesAvailable, total: maxVolunteers })}
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="star" flair="filled" /></div>
                    {location}
                  </div>
                  <div className="NLdoetProject__sidebar_section">
                    <b>{t('nldoet.project.contact')}</b>
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="home" flair="filled" /></div>
                    <CoreLink href={website}>
                      {organization}
                    </CoreLink>
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="person" flair="filled" /></div>
                    {contactGender !== 'neutraal' && (
                      contactGender
                    )} {contactInitials} {contactSurname}
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="mail" flair="filled" /></div>
                    <CoreLink href={`mailto:${contactEmail}`}>
                      {contactEmail}
                    </CoreLink>
                  </div>
                  <div className="NLdoetProject__sidebar_item">
                    <div className="NLdoetProject__sidebar_item__icon"><Icon name="call" flair="filled" /></div>
                    <CoreLink href={`tel:${contactPhoneNumber}`}>
                      {contactPhoneNumber}
                    </CoreLink>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-8 col-xl-9">
                {result && (
                  <>
                    <h3>{t('nldoet.project.result')}</h3>
                    <WysiwygField
                      property="schema:text"
                      html={result}
                    />
                  </>
                )}

                {extraInformation && (
                  <>
                    <h3>{t('nldoet.project.extra_information')}</h3>
                    <WysiwygField
                      property="schema:text"
                      html={extraInformation}
                    />
                  </>
                )}
                <Share />
              </div>
            </div>

            {placesAvailable > 0 && (
              <div className="row">
                <div className="col-12">
                  <PatternBlock className="DefaultPatternBlock BottomBlock">
                    <h2>{t('nldoet.project.registration_title')}</h2>

                    <div className="DefaultPatternBlock__buttons">
                      <ButtonWrapper
                        // @ts-ignore
                        href={`${APP_OPTIONS.parameters.nldoetSubscribeUrls.solo[isSaturday ? 'saturday' : 'friday']}${params}`}
                        type="secondary"
                      >
                        {t('nldoet.project.register_individual')}
                      </ButtonWrapper>

                      <ButtonWrapper
                        // @ts-ignore
                        href={`${APP_OPTIONS.parameters.nldoetSubscribeUrls.group[isSaturday ? 'saturday' : 'friday']}${params}`}
                        type="secondary"
                        inverted
                      >
                        {t('nldoet.project.register_group')}
                      </ButtonWrapper>
                    </div>

                  </PatternBlock>
                </div>
              </div>
            )}

          </div>
          <div className="col-12 col-lg-8 offset-lg-2">
            <div className="NLdoetProject__related">
              <h2>{t('nldoet.project.related')}</h2>
              <SearchKit
                uri={APP_OPTIONS?.parameters.apolloSearchUrl}
                query={query}
                pageSize={4}
                useRouting={false}
                defaultFilters={[
                  {
                    identifier: "radius",
                    geoRadius: {
                      radius: 5000,
                      center: {
                        lat,
                        lon,
                      },
                    },
                  },
                ]}
              >
                {
                  (data: any) => data && (
                    <MapListSearchResults
                      data={data.nLDoetSearch}
                    />
                  )
                }
              </SearchKit>
              <ButtonWrapper
                className="NLdoetProject__related__button"
                type="primary"
                inverted
                href="/kaart"
              >
                {t('to_overview')}
              </ButtonWrapper>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default NLdoetProject;
